import { reattach, type DetachedMesh } from './modelOps';

interface DeleteBatch {
  ids: number[];
  detached: DetachedMesh[];
}

/**
 * Undo stack for session-only element deletion.
 *
 * Deletions are stored as batches so one undo restores one delete action,
 * however many meshes that action removed. It owns the detached meshes: they
 * are out of the scene graph but still referenced here, which is what keeps
 * an undo possible and what keeps their geometry out of the disposal walk
 * until they are reattached.
 */
export class DeleteHistory {
  private batches: DeleteBatch[] = [];

  get canUndo(): boolean {
    return this.batches.length > 0;
  }

  /** Every deleted id, oldest deletion first. */
  get deletedIDs(): number[] {
    return this.batches.flatMap((b) => b.ids);
  }

  push(ids: number[], detached: DetachedMesh[]): void {
    this.batches.push({ ids, detached });
  }

  /** Reattaches the most recent deletion; returns the ids it restored. */
  undo(): number[] {
    const batch = this.batches.pop();
    if (!batch) return [];
    reattach(batch.detached);
    return batch.ids;
  }

  /** Reattaches everything; returns all restored ids, oldest deletion first. */
  restoreAll(): number[] {
    const ids = this.deletedIDs;
    for (const batch of this.batches) reattach(batch.detached);
    this.batches = [];
    return ids;
  }
}
